import { memo, useMemo } from 'react'

import { poleOfInaccessibility } from '@/geo/polygon'
import { pctToView, type Poly } from '@/geo/types'

/**
 * One region drawn in the canvas's percent space (`viewBox="0 0 100 56.25"`,
 * so percent-y is multiplied by `0.5625` on the way in — `pctToView` is the
 * only place that factor appears).
 *
 * A low-confidence region is hatched rather than tinted darker: colour
 * already carries the *kind*, so confidence needs a second channel.
 */

export interface RegionShapeProps {
  id: string
  kind: string
  label?: string
  poly: Poly
  colour: string
  confidence: number
  locked?: boolean
  selected?: boolean
  hovered?: boolean
  hidden?: boolean
  /** A proposal's version of this shape: dashed, 10 % fill, not interactive. */
  ghost?: boolean
  hasError?: boolean
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

const ringToPoints = (ring: readonly [number, number][]): string => ring.map((p) => pctToView(p as [number, number]).join(',')).join(' ')

/** `<polygon>` cannot hold holes, so a poly with holes becomes a `<path>` with `fill-rule: evenodd`. */
function polyToPath(poly: Poly): string {
  const ring = (r: readonly [number, number][]): string => {
    const pts = r.map((p) => pctToView(p as [number, number]))
    const first = pts[0]
    if (!first) return ''
    return `M${first[0]},${first[1]}` + pts.slice(1).map((p) => `L${p[0]},${p[1]}`).join('') + 'Z'
  }
  return [ring(poly.outer), ...(poly.holes ?? []).map(ring)].join(' ')
}

export const LOW_CONFIDENCE = 0.5

export const RegionShape = memo(function RegionShape({
  id,
  kind,
  label,
  poly,
  colour,
  confidence,
  locked,
  selected,
  hovered,
  hidden,
  ghost,
  hasError,
  onPointerDown,
}: RegionShapeProps) {
  const hasHoles = (poly.holes?.length ?? 0) > 0
  const d = useMemo(() => (hasHoles ? polyToPath(poly) : ''), [hasHoles, poly])
  const points = useMemo(() => (hasHoles ? '' : ringToPoints(poly.outer)), [hasHoles, poly])
  const centre = useMemo(() => pctToView(poleOfInaccessibility(poly, 0.8)), [poly])

  if (hidden) return null

  const low = confidence < LOW_CONFIDENCE
  const fillOpacity = ghost ? 0.1 : selected ? 0.34 : hovered ? 0.26 : 0.18
  const strokeWidth = (selected ? 0.5 : hovered ? 0.4 : 0.28) / 1

  const common = {
    'data-id': id,
    'data-kind': kind,
    fill: low && !ghost ? `url(#hatch-${cssId(kind)})` : colour,
    fillOpacity,
    stroke: colour,
    strokeWidth,
    strokeLinejoin: 'round' as const,
    strokeDasharray: ghost ? '1.2 0.8' : undefined,
    vectorEffect: 'non-scaling-stroke' as const,
    style: { pointerEvents: ghost ? ('none' as const) : ('auto' as const), cursor: locked ? 'not-allowed' : 'pointer' },
    onPointerDown,
    role: 'img',
    'aria-label': `${label ?? kind}${locked ? ', locked' : ''}${low ? ', low confidence' : ''}`,
  }

  return (
    <g>
      <defs>
        <pattern id={`hatch-${cssId(kind)}`} width="2" height="2" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="2" height="2" fill={colour} fillOpacity="0.12" />
          <line x1="0" y1="0" x2="0" y2="2" stroke={colour} strokeWidth="0.6" />
        </pattern>
      </defs>
      {hasHoles ? <path d={d} fillRule="evenodd" {...common} /> : <polygon points={points} {...common} />}
      {hasError ? (
        <circle cx={centre[0]} cy={centre[1]} r={0.9} fill="var(--color-danger)" stroke="white" strokeWidth={0.2} pointerEvents="none" />
      ) : null}
      {locked && !ghost ? <circle cx={centre[0]} cy={centre[1]} r={0.55} fill={colour} stroke="white" strokeWidth={0.18} pointerEvents="none" /> : null}
    </g>
  )
})

/** Kind ids contain `:` which is not valid in a fragment identifier. */
function cssId(kind: string): string {
  return kind.replace(/[^a-z0-9_-]/gi, '_')
}
